import { Component, Input, OnDestroy, OnInit, ViewChild } from "@angular/core";
import { FormBuilder, UntypedFormControl, Validators } from "@angular/forms";
import { MatDialog } from "@angular/material/dialog";
import { MatPaginator } from "@angular/material/paginator";
import { MatTable } from "@angular/material/table";
import { Router } from "@angular/router";
import {
  DataType,
  ICareplan,
  IChargeItemsInfo,
  IPatientUser,
  IPrescriptionInCareplan,
  IPrescriptionInfosResponse,
} from "@comunicare/comunicare-interfaces";
import moment from "moment";
import { BehaviorSubject, combineLatest } from "rxjs";
import { first, map, takeUntil } from "rxjs/operators";
import { BillingDataSource } from "src/app/pages/billing/billing.datasource";
import { BillingService } from "src/app/pages/billing/services/billing.service";
import { CareplansService } from "src/app/providers/careplans.service";
import { PrescriptionService } from "src/app/providers/prescription.service";
import { ResponsiveDialogService } from "src/app/providers/responsive-dialog.service";
import { ResponsiveService } from "src/app/providers/responsive.service";
import { SessionService } from "src/app/providers/session.service";
import { UserService } from "src/app/providers/user.service";
import { WidgetBaseComponent } from "../base/widget-base/widget-base.component";
import { WidgetActionConfig } from "../widget-actions/widget-actions.component";
import { CareplanChoiceDialogComponent } from "../widgets/pending-prescriptions-widget/careplan-choice-dialog/careplan-choice-dialog.component";
import { PendingPrescriptionsDataSource } from "../widgets/pending-prescriptions-widget/pending-prescriptions-widget-datasource";
import { SelectCareplanModalComponent } from "./select-careplan-modal/select-careplan-modal.component";

@Component({
  selector: "app-patient-prescriptions",
  templateUrl: "./patient-prescriptions.component.html",
  styleUrls: ["./patient-prescriptions.component.scss", "../base/widget-base/widget-base.component.scss"],
})
export class PatientPrescriptionsComponent extends WidgetBaseComponent implements OnInit, OnDestroy {
  @ViewChild(MatPaginator) paginator: MatPaginator;
  @ViewChild(MatTable) table: MatTable<IPrescriptionInfosResponse>;
  @Input() set patientUser(pu: IPatientUser) {
    if (pu?.user?.caremateIdentifier) {
      this.pu = pu;
    }
  }

  public filterFormTable = this.fb.group({
    fromDate: new UntypedFormControl(moment().subtract(3, "month"), {
      validators: Validators.required,
      updateOn: "blur",
    }),
  });
  private get from() {
    return this.filterFormTable.get("fromDate")?.value as string;
  }
  public dataSource;
  public displayedColumns: string[] = [];
  public pu: IPatientUser;
  public currentPageSize = 5;
  public prescriptionsCount: number;
  public isLoading = false;
  private hasPrescriptions$ = new BehaviorSubject(false);
  private careplanWithPrescriptions: { careplan: ICareplan; prescriptions: IPrescriptionInCareplan[] }[] = [];
  public actions: WidgetActionConfig[] = [
    {
      type: "button",
      icon: "add",
      ariaLabel: "icon button with a pencil icon",
      condition$: combineLatest([this.hasPrescriptions$, this.userService.isAuthorized("dashboard/prescription", "POST")]).pipe(
        map(([hasPrescriptions, isAuthorized]) => hasPrescriptions && isAuthorized)
      ),
      action: (): unknown => this.addPrescription(),
    },
  ];
  private isPrescriptionTab = true;
  constructor(
    private prescriptionService: PrescriptionService,
    private fb: FormBuilder,
    protected responsiveService: ResponsiveService,
    private responsiveDialog: ResponsiveDialogService,
    private router: Router,
    private careplanService: CareplansService,
    private userService: UserService,
    private dialog: MatDialog,
    private billingService: BillingService,
    private sessionService: SessionService
  ) {
    super(responsiveService);
  }

  ngOnInit(): void {
    this.init();
    this.filterFormTable.valueChanges.pipe(takeUntil(this.onDestroy$)).subscribe(() => this.updateDate());
    this.sessionService.refreshPrescriptionsTile.pipe(takeUntil(this.onDestroy$)).subscribe(() => this.refresh());
  }

  ngOnDestroy(): void {
    super.ngOnDestroy();
  }

  private init(): void {
    if (this.isPrescriptionTab) {
      this.dataSource = new PendingPrescriptionsDataSource(this.prescriptionService);
      this.loadPendingPrescriptions();
      this.careplanService
        .list(this.pu.user.caremateIdentifier)
        .pipe(takeUntil(this.onDestroy$))
        .subscribe((careplans) => {
          careplans.forEach((careplan) => {
            this.careplanService
              .getLink2Careplan(careplan.support[0].reference)
              .pipe(first())
              .subscribe((result) => {
                const prescriptions = result.published?.prescriptions;
                if (prescriptions.length > 0) {
                  this.careplanWithPrescriptions.push({
                    careplan: careplan,
                    prescriptions: prescriptions,
                  });
                  this.hasPrescriptions$.next(true);
                }
              });
          });
        });
    } else {
      this.dataSource = new BillingDataSource(this.billingService);
      this.dataSource.loadData({
        filters: [],
        pageNumber: this.paginator?.pageIndex,
        pageSize: this.paginator?.pageSize,
        services: this.pu.patient.healthcareservice.map((s) => s.reference),
        patientId: this.pu.user.caremateIdentifier,
      });

      this.billingService
        .getInvoicesCount(
          this.pu.patient.healthcareservice.map((s) => s.reference),
          [],
          this.pu.user.caremateIdentifier
        )
        .subscribe((res) => {
          this.prescriptionsCount = res.count;
        });
    }
  }

  private refresh(): void {
    if (this.isPrescriptionTab) {
      this.loadPendingPrescriptions();
    } else {
      this.init();
    }
  }

  private updateDate(): void {
    const from = moment(this.from).format("YYYY-MM-DD");
    if (from) {
      this.loadPendingPrescriptions();
    }
  }

  private loadPendingPrescriptions() {
    this.isLoading = true;
    if (this.isPrescriptionTab) {
      this.prescriptionService
        .getPendingPrescriptionCount({
          patientCaremateId: this.pu.user.caremateIdentifier,
        })
        .pipe(first(), takeUntil(this.onDestroy$))
        .subscribe({
          next: (result) => {
            this.prescriptionsCount = result?.count;
            this.dataSource.loadData({
              patientCaremateId: this.pu.user.caremateIdentifier,
              pageNumber: 0,
              pageSize: this.currentPageSize,
              filters: [],
              date: moment(this.from).format(),
            });
            this.isLoading = false;
          },
          error: () => (this.isLoading = false),
        });
    }
  }
  public validatePrescription(prescription: IPrescriptionInfosResponse): void {
    // For every prescriptions that is not "Telemonitoring"

    if (prescription.category !== "Telemonitoring") {
      this.isLoading = true;
      this.prescriptionService
        .acceptPrescription(prescription.prescriptionId)
        .pipe(first())
        .subscribe({
          next: () => this.loadPendingPrescriptions(),
          error: () => (this.isLoading = false),
        });
      return;
    }

    // For "Telemonitoring" prescriptions
    const dialog = this.responsiveDialog.open(CareplanChoiceDialogComponent, {
      data: {
        prescription: prescription,
      },
    });

    dialog.afterClosed().subscribe((careplanId) => {
      if (!careplanId) return; // If no careplan is selected, do nothing
      this.isLoading = true;
      this.prescriptionService
        .acceptPrescription(prescription.prescriptionId, careplanId)
        .pipe(first())
        .subscribe({
          next: () => this.loadPendingPrescriptions(),
          error: () => (this.isLoading = false),
        });
    });
  }

  private addPrescription(): void {
    const dialog = this.dialog.open(SelectCareplanModalComponent, {
      data: { careplans: this.careplanWithPrescriptions.map((cp) => cp.careplan) },
    });
    dialog.afterClosed().subscribe((careplanSelected) => {
      if (careplanSelected) {
        this.router.navigate(["patientRegistration", "prescription"], {
          state: {
            directToPrescription: true,
            patientUser: this.pu,
            careplan: careplanSelected,
            careplanPrescriptions: this.careplanWithPrescriptions.find((cp) => cp.careplan._id === careplanSelected._id)?.prescriptions,
          },
        });
      }
    });
  }

  public tabChanged(): void {
    this.isPrescriptionTab = !this.isPrescriptionTab;
    this.init();
    this.displayedColumns = this.isPrescriptionTab ? [] : ["category", "code", "startDate", "endDate", "amount", "status"];
  }

  public goToBillingPage(row: IChargeItemsInfo): void {
    this.router.navigate(["invoices"], {
      state: {
        filter: {
          data: { value: row.identifier[0].value },
          propertyName: "identifier.value",
          dataType: DataType.TEXT,
        },
      },
    });
  }
}
